import type { WidgetViewMode } from "#app/widgets/dashboards/use-widget-view";
import type { WidgetViewTier } from "#app/widgets/dashboards/widget-view-policy";

/**
 * Which board a shared view's viewer is looking at: their private fork
 * (`personal`) or the shared board everyone else sees (`template`). The host
 * persists the choice per view in localStorage (`widget-view-lens:<view>`) so
 * a fork holder lands back on their fork after a reload. The stored value
 * `"template"` is a persisted VALUE — the UI labels it "Shared".
 */
export type WidgetViewLens = "personal" | "template";

/**
 * Tier + lens → store mode, resolved against the viewer's runtime state.
 * Pure so the node harness can truth-table it (the feature's pure-module
 * rule). ONE rule for every view:
 *
 * - `private-owner` (My Work) edits its own board directly — plain
 *   write-through autosave; the view already IS personal, so no fork/lens.
 * - A shared view's TEMPLATE lens shows the shared board: editable (staged
 *   behind "Save for the team") for `canWrite` viewers, read-only otherwise
 *   (`autosave` mode with every edit affordance gated off by the host).
 * - A shared view's PERSONAL lens shows the viewer's fork. Non-writers REST
 *   here even before a fork exists (the shared board shows read-only until
 *   Personalize arms editing — resting avoids mode churn on the common
 *   forkless path). Writers stay on `staged` until a fork exists or
 *   Personalize is armed — their default job is editing the template.
 */
export function resolveWidgetViewMode(input: {
  tier: WidgetViewTier;
  canWrite: boolean;
  lens: WidgetViewLens;
  hasFork: boolean;
  personalizing: boolean;
}): WidgetViewMode {
  if (input.tier === "private-owner") return "autosave";
  if (input.lens === "template") return input.canWrite ? "staged" : "autosave";
  if (!input.canWrite) return "personal";
  return input.hasFork || input.personalizing ? "personal" : "staged";
}
